/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { useContext, useEffect } from "react"
import { Row, Col, Button } from "@duxton-web/ui"
import { ChevronLeftUniversal, CloseUniversal } from "@duxton-web/icons"
import { Form } from "grab-ui"
import { BULK_UPLOAD_EXPORT_FILE_FIELDS } from "constants/menu_bulk_upload"
import {
  BULK_UPLOAD_TYPE,
  BulkUploadContext,
  DRAWER_STATES,
} from "pages/menu/detail/bulkUpload/BulkUploadContext"
import { useTranslations } from "common/utils/i18n"
import { useScribeUploadAndPublishDrawer } from "pages/menu/detail/bulkUpload/scribe/useScribeUploadAndPublishDrawer"
import ExportFileFailed from "pages/menu/detail/bulkUpload/bulkUploadNotifications/ExportFileFailed"
import { drawerFooterWrap } from "../drawerStartAndPublish/styles"
import { useInventoryCategories } from "../hooks/useInventoryCategories"
import InventoryCategorySelection from "./InventoryCategorySelection"
import { useExportFileButtonProps } from "../hooks/useExportFileButtonProps"

const drawerDownloadCatalogueWrapCss = css`
  padding: 24px 24px 0;
`

const backIcon = css`
  cursor: pointer;
  font-size: 16px;
`

const drawerContentWrapCss = css`
  padding-bottom: 83px; // footer height
  background-color: #fff;
`

export const DrawerDownloadCatalogue = (props: FormComponentProps) => {
  const {
    trackDownloadCataloguePageView,
    trackBackClick,
  } = useScribeUploadAndPublishDrawer()
  const [
    drawerTitleInventoryText,
    downloadFileButtonText,
    drawerTitleMenuText,
  ] = useTranslations([
    "bulkUpload.drawers.downloadInventory.title",
    "bulkUpload.drawers.downloadInventory.downloadFileButton",
    "bulkUpload.drawers.downloadItem.title",
  ])

  const { form } = props
  const {
    bulkUploadType,
    drawerStateTransitionDisabled,
    setDrawerState,
    setDrawerVisible,
    setDrawerStateTransitionDisabled,
  } = useContext(BulkUploadContext)

  const filteredCategoryName = form.getFieldValue(
    BULK_UPLOAD_EXPORT_FILE_FIELDS.categoryNameInput,
  )
  const selectedCategoryIDs = form.getFieldValue(
    BULK_UPLOAD_EXPORT_FILE_FIELDS.categoryCheckboxGroup,
  )
  const selectedItemStatus = form.getFieldValue(
    BULK_UPLOAD_EXPORT_FILE_FIELDS.categoryItemStatus,
  )

  const selectedItemProperty = form.getFieldValue(
    BULK_UPLOAD_EXPORT_FILE_FIELDS.menuItemProperty,
  )

  const { data: inventoryCategories } = useInventoryCategories(
    filteredCategoryName,
  )
  const {
    isExportBtnEnabled,
    isFileExporting,
    onClickExportFile,
    exportFileErrorPopupVisible,
    onCloseExportFileErrorPopup,
  } = useExportFileButtonProps(
    selectedCategoryIDs,
    selectedItemStatus,
    selectedItemProperty,
  )

  const onClickCloseIcon = () => {
    if (!drawerStateTransitionDisabled) {
      setDrawerState(DRAWER_STATES.START)
      return
    }

    setDrawerVisible(false)
    setDrawerStateTransitionDisabled(false)
    // prevent drawer state transition flashing
    setTimeout(() => {
      setDrawerState(DRAWER_STATES.START)
    }, 200)
  }

  useEffect(() => {
    trackDownloadCataloguePageView()
  }, [trackDownloadCataloguePageView])

  return (
    <div>
      <div>
        <Row
          css={css`
            padding: 16px 24px;
            border-bottom: 1px solid rgba(5, 5, 5, 0.06);
          `}
          gutter={12}
          align="middle"
        >
          <Col>
            <div
              css={backIcon}
              onClick={() => {
                onClickCloseIcon()
                trackBackClick()
              }}
            >
              {drawerStateTransitionDisabled ? (
                <CloseUniversal />
              ) : (
                <ChevronLeftUniversal />
              )}
            </div>
          </Col>
          <Col>
            <span
              css={css`
                text-transform: uppercase;
                line-height: 1.5;
                font-size: 16px;
                font-weight: 600;
              `}
            >
              {bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY
                ? drawerTitleInventoryText
                : drawerTitleMenuText}
            </span>
          </Col>
        </Row>
      </div>
      <div css={drawerDownloadCatalogueWrapCss}>
        <div css={drawerContentWrapCss}>
          <InventoryCategorySelection
            inventoryCategories={inventoryCategories}
            form={form}
          />
        </div>
        <div css={drawerFooterWrap}>
          <Button
            block
            disabled={!isExportBtnEnabled}
            type="primary"
            onClick={onClickExportFile}
            loading={isFileExporting}
          >
            {downloadFileButtonText}
          </Button>
        </div>
        <ExportFileFailed
          visible={exportFileErrorPopupVisible}
          onCloseCallback={onCloseExportFileErrorPopup}
        />
      </div>
    </div>
  )
}

export default Form.create()(DrawerDownloadCatalogue)
